/* PureState header v2: hide on scroll down / reveal on scroll up at every viewport (ribbon + header row together, the
   section wrapper is position: sticky and gets .ps-header--hidden = translateY(-100%)), the ribbon rotator, the menu
   drawer helpers and two root vars:
   --ps-header-height  = scroll offset: the combined ribbon + row height while visible; 0px while hidden by a
                         scroll-down (ps-base.css scroll-margin-top / scroll-padding-top).
   --ps-topbar-height  = stable layout offset for older consumers (hero sticky column, ingredient tabs): the combined
                         height whenever the element is connected, regardless of hidden state; 0px only when it is not.
   Hide/reveal hysteresis: hide after data-hide-after px of continuous downward scroll (and only once the page has
   scrolled past the header), reveal after data-reveal-after px of upward scroll; always visible at scrollY 0, in the
   theme editor, while the menu drawer is open or while keyboard focus is inside the header.
   Ribbon: setInterval(data-interval s) advance with a vertical slide (CSS classes), paused on hover / focus-within /
   hidden tab, arrows switch immediately, prefers-reduced-motion → no timer and no slide; only the active message is
   exposed to assistive tech (the others carry aria-hidden + inert).
   In-page anchor navigation (drawer links, footer links, ps-cta scroll_to_buybox, hashchange): before an upward jump
   the header is revealed so the offset is already in place; before a downward jump past the header it is hidden so
   the target lands at the top with no gap; after the scroll settles the target is re-aligned once to header height
   (0 while hidden) + the target's own computed scroll-margin-top (at least the 1.2rem of ps-base.css [id]) — so a
   section can declare an extra offset (the ingredients panels add their stuck tab bar) and the landing mirrors what
   the browser's scroll-padding + scroll-margin produce. This also corrects the drift caused by content-visibility:
   auto sections rendering (and growing) while the smooth scroll passes them. A drawer link click closes the drawer
   first so the target is visible. No copy in JS (labels come from data-* attributes rendered by Liquid). */
(function () {
  if (customElements.get('ps-header')) return;
  var doc = document, root = doc.documentElement;
  var dm = !!(window.Shopify && Shopify.designMode);
  var GAP = 12; /* = the 1.2rem added to scroll-margin-top in ps-base.css (1rem = 10px), the minimum landing gap */
  /* The gap the target lands below the header: its computed scroll-margin-top (any section may raise it, e.g. the
     ingredients panels add their stuck tab bar), never less than the base 1.2rem. */
  var landingGap = function (target) {
    var m = 0;
    try { m = parseFloat(getComputedStyle(target).scrollMarginTop) || 0; } catch (e) { m = 0; }
    return Math.max(m, GAP);
  };
  var SLIDE_MS = 300; /* ≥ the 250 ms CSS transition */
  var mqReduce = window.matchMedia ? matchMedia('(prefers-reduced-motion: reduce)') : null;
  var reduced = function () { return !!(mqReduce && mqReduce.matches); };
  var px = function (n) { return Math.max(0, Math.round(n)) + 'px'; };
  var num = function (v, d) { var n = parseFloat(v); return isFinite(n) ? n : d; };
  var setVars = function (offset, stable) {
    root.style.setProperty('--ps-header-height', px(offset));
    root.style.setProperty('--ps-topbar-height', px(stable));
  };
  var addMq = function (mq, fn) { if (!mq) return; if (mq.addEventListener) mq.addEventListener('change', fn); else if (mq.addListener) mq.addListener(fn); };
  var removeMq = function (mq, fn) { if (!mq) return; if (mq.removeEventListener) mq.removeEventListener('change', fn); else if (mq.removeListener) mq.removeListener(fn); };
  var byHash = function (hash) {
    if (!hash || hash.charAt(0) !== '#' || hash.length < 2) return null;
    var id = hash.slice(1);
    try { id = decodeURIComponent(id); } catch (e) {}
    return doc.getElementById(id) || null;
  };

  customElements.define('ps-header', class extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section') || this.parentElement;
      this.designMode = dm || this.hasAttribute('data-design-mode');
      this.hideEnabled = this.dataset.hideOnScroll !== 'false';
      this.hideAfter = Math.max(0, num(this.dataset.hideAfter, 80));
      this.revealAfter = Math.max(0, num(this.dataset.revealAfter, 40));
      this.measure = this.measure.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onHash = this.onHash.bind(this);
      this.onReduce = this.onReduce.bind(this);
      this.onVisibility = this.onVisibility.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.lastY = window.scrollY || 0;
      this.downFrom = this.lastY;
      this.upFrom = this.lastY;
      this.isHidden = false;
      this.settleTimer = null;
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(this.measure);
        this.ro.observe(this);
      }
      window.addEventListener('resize', this.measure, { passive: true });
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('hashchange', this.onHash);
      doc.addEventListener('click', this.onClick, true);
      doc.addEventListener('visibilitychange', this.onVisibility);
      this.addEventListener('focusin', this.onFocusIn);
      addMq(mqReduce, this.onReduce);
      this.initDrawer();
      this.initRibbon();
      this.measure();
    }
    disconnectedCallback() {
      if (this.ro) this.ro.disconnect();
      if (this.settleTimer) clearTimeout(this.settleTimer);
      this.stopRibbon();
      if (this.leaveTimer) clearTimeout(this.leaveTimer);
      window.removeEventListener('resize', this.measure);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('hashchange', this.onHash);
      doc.removeEventListener('click', this.onClick, true);
      doc.removeEventListener('visibilitychange', this.onVisibility);
      this.removeEventListener('focusin', this.onFocusIn);
      removeMq(mqReduce, this.onReduce);
      if (this.section) this.section.classList.remove('ps-header--hidden', 'ps-header--animate');
      setVars(0, 0);
    }

    /* ---- hide / reveal ---- */
    measure() {
      var h = this.getBoundingClientRect().height;
      setVars(this.isHidden ? 0 : h, h);
    }
    drawerOpen() {
      return !!(this.drawerDetails && this.drawerDetails.open);
    }
    /* Keyboard focus inside the header (a mouse click on a ribbon arrow must not pin the header). */
    keyboardFocusInside() {
      try { return !!this.querySelector(':focus-visible'); } catch (e) { return false; }
    }
    /* Tab into a hidden header (logo / CTA / cart / menu): reveal it so the focus stop is on screen; the hysteresis
       trackers restart at the current position so the reveal is not undone by the next scroll tick. */
    onFocusIn() {
      if (!this.isHidden || !this.keyboardFocusInside()) return;
      this.setHidden(false);
      var y = window.scrollY || 0;
      this.downFrom = y; this.upFrom = y; this.lastY = y;
    }
    onScroll() {
      var y = window.scrollY || 0;
      if (this.designMode || !this.hideEnabled || this.drawerOpen()) { this.lastY = y; return; }
      if (y <= 0) {
        this.setHidden(false);
        this.lastY = 0; this.downFrom = 0; this.upFrom = 0;
        return;
      }
      var h = this.offsetHeight || 0;
      if (y > this.lastY) {
        this.upFrom = y;
        if (!this.isHidden && y - this.downFrom >= this.hideAfter && y > h && !this.keyboardFocusInside()) this.setHidden(true);
      } else if (y < this.lastY) {
        this.downFrom = y;
        if (this.isHidden && this.upFrom - y >= this.revealAfter) this.setHidden(false);
      }
      this.lastY = y;
    }
    setHidden(v) {
      if (this.isHidden === v) return;
      this.isHidden = v;
      if (this.section) {
        this.section.classList.toggle('ps-header--animate', !reduced());
        this.section.classList.toggle('ps-header--hidden', v);
      }
      this.measure();
    }
    onReduce() {
      if (this.ribbon) this.ribbon.classList.toggle('ps-ribbon--animate', !reduced());
      if (reduced()) this.stopRibbon(); else this.startRibbon();
    }
    onVisibility() {
      if (doc.hidden) this.stopRibbon(); else this.startRibbon();
    }

    /* ---- menu drawer (Dawn HeaderDrawer does open / focus trap / ESC; this adds labels + close-on-anchor) ---- */
    initDrawer() {
      var self = this;
      this.drawerDetails = this.querySelector('#Details-menu-drawer-container');
      if (!this.drawerDetails) return;
      this.drawerSummary = this.drawerDetails.querySelector('summary');
      this.drawerDetails.addEventListener('toggle', function () { self.syncDrawerLabel(); });
      this.syncDrawerLabel();
    }
    syncDrawerLabel() {
      var s = this.drawerSummary, d = this.drawerDetails;
      if (!s || !d) return;
      var label = d.open ? s.getAttribute('data-label-close') : s.getAttribute('data-label-open');
      if (label) s.setAttribute('aria-label', label);
      s.setAttribute('aria-expanded', d.open ? 'true' : 'false');
      if (d.open) this.setHidden(false);
    }
    closeDrawer() {
      var d = this.drawerDetails;
      if (!d || !d.open) return;
      var hd = d.closest('header-drawer');
      d.classList.remove('menu-opening');
      d.removeAttribute('open');
      doc.body.classList.remove('overflow-hidden-' + ((hd && hd.dataset.breakpoint) || 'desktop'));
      if (this.section) this.section.classList.remove('menu-open');
      if (hd && hd.onResize) window.removeEventListener('resize', hd.onResize);
      if (typeof removeTrapFocus === 'function') removeTrapFocus();
      this.syncDrawerLabel();
    }

    /* ---- ribbon rotator ---- */
    initRibbon() {
      var self = this;
      this.ribbon = this.querySelector('[data-ps-ribbon]');
      if (!this.ribbon) return;
      this.items = [].slice.call(this.ribbon.querySelectorAll('[data-ps-ribbon-item]'));
      this.index = 0;
      for (var i = 0; i < this.items.length; i += 1) if (this.items[i].classList.contains('is-active')) this.index = i;
      this.intervalMs = Math.max(1, num(this.ribbon.dataset.interval, 5)) * 1000;
      this.ribbon.classList.toggle('ps-ribbon--animate', !reduced());
      if (this.items.length < 2) return;
      this.paused = false;
      var prev = this.ribbon.querySelector('[data-ps-ribbon-prev]');
      var next = this.ribbon.querySelector('[data-ps-ribbon-next]');
      if (prev) prev.addEventListener('click', function () { self.go(self.index - 1, -1); self.restartRibbon(); });
      if (next) next.addEventListener('click', function () { self.go(self.index + 1, 1); self.restartRibbon(); });
      this.ribbon.addEventListener('mouseenter', function () { self.paused = true; self.stopRibbon(); });
      this.ribbon.addEventListener('mouseleave', function () { self.paused = false; self.startRibbon(); });
      this.ribbon.addEventListener('focusin', function () { self.paused = true; self.stopRibbon(); });
      this.ribbon.addEventListener('focusout', function (evt) {
        if (evt.relatedTarget && self.ribbon.contains(evt.relatedTarget)) return;
        self.paused = false;
        self.startRibbon();
      });
      this.startRibbon();
    }
    startRibbon() {
      var self = this;
      if (!this.ribbon || !this.items || this.items.length < 2) return;
      if (this.timer || this.paused || this.editorHold || reduced() || doc.hidden) return;
      this.timer = setInterval(function () { self.go(self.index + 1, 1); }, this.intervalMs);
    }
    stopRibbon() {
      if (this.timer) { clearInterval(this.timer); this.timer = null; }
    }
    restartRibbon() {
      this.stopRibbon();
      this.startRibbon();
    }
    go(to, dir) {
      var items = this.items, n = items ? items.length : 0;
      if (n < 2) return;
      to = ((to % n) + n) % n;
      if (to === this.index) return;
      var cur = items[this.index], nxt = items[to];
      if (this.leaveTimer) { clearTimeout(this.leaveTimer); this.leaveTimer = null; }
      for (var i = 0; i < n; i += 1) items[i].classList.remove('is-leaving', 'is-down', 'is-above');
      if (dir < 0) nxt.classList.add('is-above');
      void nxt.offsetHeight; /* commit the start position before the transition */
      cur.classList.remove('is-active');
      cur.classList.add('is-leaving');
      if (dir < 0) cur.classList.add('is-down');
      nxt.classList.remove('is-above');
      nxt.classList.add('is-active');
      this.expose(nxt, true);
      this.expose(cur, false);
      this.leaveTimer = setTimeout(function () { cur.classList.remove('is-leaving', 'is-down'); }, reduced() ? 0 : SLIDE_MS);
      this.index = to;
    }
    expose(item, on) {
      var links = item.querySelectorAll('a, button');
      if (on) {
        item.removeAttribute('aria-hidden');
        item.removeAttribute('inert');
        for (var i = 0; i < links.length; i += 1) links[i].removeAttribute('tabindex');
      } else {
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('inert', '');
        for (var j = 0; j < links.length; j += 1) links[j].setAttribute('tabindex', '-1');
      }
    }

    /* ---- in-page anchor navigation ---- */
    onClick(evt) {
      if (evt.defaultPrevented) return;
      if (evt.button || evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey) return;
      var el = evt.target && evt.target.closest && evt.target.closest('a[href], [data-ps-cta-action="scroll_to_buybox"]');
      if (!el) return;
      /* A tab control is not in-page navigation: it swaps a panel in place and its own script calls preventDefault.
         This listener is on the capture phase, so defaultPrevented is still false here — skip tabs explicitly or the
         page would be re-aligned to the revealed panel and scroll the tab bar out of view (F-49, 2026-09-07). */
      if (el.getAttribute('role') === 'tab') return;
      if (this.drawerDetails && el.tagName === 'A' && this.drawerDetails.contains(el) && el !== this.drawerSummary) this.closeDrawer();
      if (this.designMode) return;
      var target = this.anchorTarget(el);
      if (target) this.prepare(target);
    }
    onHash() {
      if (this.designMode) return;
      var target = byHash(location.hash || '');
      if (target) this.settle(target);
    }
    anchorTarget(el) {
      if (el.getAttribute('data-ps-cta-action') === 'scroll_to_buybox') {
        return byHash(el.getAttribute('data-ps-cta-target') || el.getAttribute('href') || '#buy-box');
      }
      var url;
      try { url = new URL(el.getAttribute('href') || '', location.href); } catch (e) { return null; }
      if (url.origin !== location.origin || url.pathname !== location.pathname || url.search !== location.search) return null;
      return byHash(url.hash);
    }
    /* Runs before the browser (or ps-cta) scrolls: put the header in the state it will end up in, so the
       scroll-margin the browser reads matches the header that is on screen when the scroll finishes. */
    prepare(target) {
      var top = target.getBoundingClientRect().top;
      var h = this.offsetHeight || 0;
      var y = window.scrollY || 0;
      var gap = landingGap(target);
      if (top < 0 || !this.hideEnabled) this.setHidden(false);
      else if (top > h + gap && y + top - gap > h) this.setHidden(true);
      this.downFrom = y; this.upFrom = y; this.lastY = y;
      this.settle(target);
    }
    /* After the scroll settles (smooth scrolling included): re-align the target once — header height (0 while hidden)
       + the target's computed scroll-margin-top (≥ 1.2rem), read at settle time so a panel revealed by the hash (tab
       switch) reports its final margin. Covers both a target left under the header / a section's own sticky bar and
       the drift from content-visibility: auto sections that render while the page scrolls past them. Other scripts may
       call it directly (ps-ingredients.js does after a deep link into a hidden panel). */
    settle(target) {
      var self = this, last = window.scrollY || 0, still = 0, tries = 0;
      if (this.settleTimer) clearTimeout(this.settleTimer);
      var tick = function () {
        self.settleTimer = null;
        var y = window.scrollY || 0;
        if (y === last) still += 1; else { still = 0; last = y; }
        tries += 1;
        if (still < 2 && tries < 45) { self.settleTimer = setTimeout(tick, 80); return; }
        self.downFrom = y; self.upFrom = y; self.lastY = y;
        if (!doc.contains(target)) return;
        var top = target.getBoundingClientRect().top, h = self.isHidden ? 0 : (self.offsetHeight || 0);
        var delta = top - h - landingGap(target);
        if (Math.abs(delta) > 1 && y + delta >= 0) window.scrollBy({ top: delta, left: 0, behavior: 'instant' });
        /* the correction is not a user scroll: keep the hysteresis trackers at the new position */
        var ny = window.scrollY || 0;
        self.downFrom = ny; self.upFrom = ny; self.lastY = ny;
      };
      this.settleTimer = setTimeout(tick, 80);
    }
  });

  /* Editor: the section re-renders on shopify:section:load (the element reconnects); block selection shows the
     selected ribbon message (rotation held) or opens the drawer for a selected menu link. */
  var headerOf = function (ev) {
    var t = ev.target;
    return t && t.closest ? t.closest('.shopify-section') : null;
  };
  doc.addEventListener('shopify:section:load', function () {
    doc.querySelectorAll('ps-header').forEach(function (el) { if (el.measure) el.measure(); });
  });
  doc.addEventListener('shopify:section:unload', function (ev) {
    if (ev.target && ev.target.querySelector && ev.target.querySelector('ps-header')) setVars(0, 0);
  });
  doc.addEventListener('shopify:block:select', function (ev) {
    var sec = headerOf(ev), el = sec && sec.querySelector('ps-header');
    if (!el || !ev.target) return;
    var item = ev.target.closest('[data-ps-ribbon-item]');
    if (item && el.items) {
      var i = el.items.indexOf(item);
      el.editorHold = true;
      el.stopRibbon();
      if (i > -1 && i !== el.index) el.go(i, i > el.index ? 1 : -1);
      return;
    }
    if (el.drawerDetails && el.drawerDetails.contains(ev.target) && !el.drawerDetails.open && el.drawerSummary) el.drawerSummary.click();
  });
  doc.addEventListener('shopify:block:deselect', function (ev) {
    var sec = headerOf(ev), el = sec && sec.querySelector('ps-header');
    if (!el) return;
    if (ev.target && ev.target.closest && ev.target.closest('[data-ps-ribbon-item]')) {
      el.editorHold = false;
      el.startRibbon();
    }
  });
})();
